import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, TrendingUp, MousePointer, ShoppingCart } from "lucide-react";
import { CHANNELS } from "./constants";
import type { Campaign } from "./types";

interface Props {
  campaigns: Campaign[];
}

export function ChannelStats({ campaigns }: Props) {
  const channelStats = CHANNELS.map((ch) => {
    const chCampaigns = campaigns.filter((c) => c.channel === ch.id);
    const totalSpent = chCampaigns.reduce((s, c) => s + c.spent, 0);
    const totalClicks = chCampaigns.reduce((s, c) => s + c.clicks, 0);
    const totalConversions = chCampaigns.reduce((s, c) => s + c.conversions, 0);
    const avgCpc = totalClicks > 0 ? totalSpent / totalClicks : 0;
    const cpl = totalConversions > 0 ? totalSpent / totalConversions : 0;
    const convRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    return { ...ch, totalSpent, totalClicks, totalConversions, avgCpc, cpl, convRate, count: chCampaigns.length };
  }).filter((s) => s.count > 0);

  if (channelStats.length === 0) return null;

  return (
    <Card className="p-5 bg-card/50 backdrop-blur border-border/30">
      <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-primary" /> Statistiques par canal
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {channelStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.id} className="rounded-xl border border-border/40 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: stat.color + "22" }}>
                  <Icon className="h-3.5 w-3.5" style={{ color: stat.color }} />
                </div>
                <span className="text-[10px] font-medium truncate">{stat.name}</span>
              </div>
              <div className="grid grid-cols-2 gap-1 text-[9px]">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <MousePointer className="h-2.5 w-2.5" />
                  <span>{stat.totalClicks.toLocaleString("fr-FR")} clics</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <ShoppingCart className="h-2.5 w-2.5" />
                  <span>{stat.totalConversions} conv.</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <span>CPC: {stat.avgCpc.toFixed(2)}€</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <TrendingUp className="h-2.5 w-2.5" />
                  <span>{stat.convRate.toFixed(1)}%</span>
                </div>
              </div>
              {stat.totalSpent > 0 && (
                <Badge variant="outline" className="text-[8px] w-full justify-center">
                  {stat.totalSpent.toLocaleString("fr-FR")}€ investis → CPL: {stat.cpl.toFixed(2)}€
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
